import { DynamicFormRef, makeFormInputsUneditable } from "@mavichpe/dynamic-forms";
import { useEffect, useRef, useState } from "react";
import styles from "./style.module.scss";
import GenericForm, { ConfirmationConfig } from "../GenericForm";
import { CreditnoteApi, CreditnoteDataType } from "@/api/CreditnoteApi";
import { Col, Row } from "reactstrap";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { selectEDocumentSummary } from "@/store/slices/utils/eDocumentSelectors";
import { clearDocument, loadProducts, setDocumentNoteTotal, setDocumentReferences, setInvoiceReference } from "@/store/slices/eDocumentSlides";
import { StoredReference } from "@utils/DocumentReferences";
import LoadingScreen from "@components/content/LoadingScreen";
import EdocumentProductList from "@components/dynamic/EdocumentProductList";
import { showAlert } from "@utils/Alert";
import { getCurrentDateTime } from "@utils/Api";
import { CreditnoteProductDataType } from "@/api/CreditnoteProductApi";
import formConfig from "./form";
import { generateNewEDocumentProductsData, sendECreditnoteData } from "@utils/EDocument";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { InvoiceFormData, InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";
import { ClientApi } from "@/api/ClientApi";
import { CreditNoteProductRelatedAttributes, EDocumentProductDataType } from "@/formTypes/EDocumentProductDataType";
import { InvoiceFormDataKeys } from "@/generated/InvoiceFormDataKeys";
import { CurrencyIdPubSub } from "@utils/pubsubs/PubSub";
import { flushDocumentProductEdits } from "@utils/eDocumentFlush";
import debounce from "debounce";
import { getSelectProductTotal } from "@/store/slices/utils/eDocumentUtils";
import { EasyposEInvoice } from "@/atv/submitInvoice";
import { generateAndUploadPdf } from "@utils/htmlExport";
import {
    buildAutoReferenceFromInvoice,
    serializeStoredReferences,
    toInfoRefElements,
} from "@utils/DocumentReferences";
import { clearDocumentReferences } from "@/store/slices/eDocumentSlides";

type CreditnoteFormProps = {
  id?: string;
  formId: string,
  invoiceId: string,
  saveRedirectUrl: string,
  priceNote?: boolean
}

const CreditnoteForm = (props: CreditnoteFormProps) => {
  const dispatch = useAppDispatch()
  const { invoiceId, priceNote = false } = props
  const formRef = useRef<DynamicFormRef>(null);
  const invoiceData = useAppSelector((state) => state.eDocument.invoiceReference);
  const documentReferences = useAppSelector((state) => state.eDocument.documentReferences);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isValidAmount, setIsValidAmount] = useState<boolean>(false);
  const suscriptionData = useAppSelector((state) => state.suscription.suscription);
  const eDocument = useAppSelector((state) => state.eDocument);
  const documentCurrency = useAppSelector((state) => state.currency);
  const [easyposEInvoice, setEasyposEInvoice] = useState<EasyposEInvoice>()

  useEffect(() => {
    if (suscriptionData) {
      setEasyposEInvoice(new EasyposEInvoice(suscriptionData))
    }
  }, [suscriptionData]);

  useEffect(() => {
    clearForm()
  }, [])

  const loadDefaultData = async () => {
    setIsLoading(true)
    try {
      const invoice = await InvoiceApi.get(invoiceId, {
        selectionSet: [
          ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
          "currency.*",
          "client.*",
          "invoiceProducts.*",
          "invoiceProducts.product.*",
        ]
      })

      dispatch(setInvoiceReference(invoice))
      const tipoNotaDeCredito = priceNote ? "notaDeCredito" : "devolucion"
      const defaultRazon = priceNote
        ? `Nota de crédito sobre factura #${invoice.numero || ""}`
        : `Devolución sobre factura #${invoice.numero || ""}`
      const defaultRef = buildAutoReferenceFromInvoice(invoice, tipoNotaDeCredito, defaultRazon)
      dispatch(setDocumentReferences([defaultRef]))
      const { id, ...restInvoice } = invoice

      const client = await ClientApi.get(`${invoice.clientId}`)
      formRef.current?.resetFormValue({
        ...restInvoice,
        client_code: client.codigo
      })
      if (invoice.invoiceProducts) {
        const productos = invoice.invoiceProducts.map((ip: EDocumentProductDataType) => {
          let product = {
            ...ip,
          }
          if (priceNote) {
            product = {
              ...product,
              selected: "true",
              price: 0,
              subtotal: 0,
              descriptionFact: ip.description,
              cantFact: ip.cant,
              priceFact: ip.price,
              subtotalFact: ip.subtotal,
              impuestosFact: ip.impuestos,
              descuentoFact: ip.descuento,
            }

          } else {
            product = {
              ...product,
              cantFact: ip.cant,
              cant: 0,
              selected: "true"
            }
          }
          return product
        })
        CurrencyIdPubSub.publish({ currencyId: `${invoice.currencyId}` })
        dispatch(loadProducts(productos))
      }
    } catch (err) {
      showAlert("danger", "No fue possible cargar la informacion de la factura")
    } finally {
      setIsLoading(false)
    }
  }

  const createCreditnote = async (formData: InvoiceDataType | CreditnoteDataType): Promise<InvoiceDataType> => {
    let description
    if ("description" in formData) {
      description = formData.description
    }

    let creditnoteData: CreditnoteDataType = {}
    const eDocumentState = flushDocumentProductEdits();
    const documentSummaryState = selectEDocumentSummary(eDocumentState);
    const { documentProducts } = eDocumentState

    if (documentProducts?.length == 0) {
      throw Error("Por favor agregue algun producto a la nota de credito")
    }

    if (documentProducts?.filter(p => Number(p.cant || 0) > 0).length == 0) {
      if (priceNote) {
        throw Error("Por favor seleccione al menos una linea para aplicar el monto de la nota")
      } else {
        throw Error("Por favor ingrese la cantidad devuelta de productos antes de continuar")
      }
    }

    if (!documentCurrency) {
      throw Error("Por favor Selecciona una moneda")
    }

    if (!description) {
      throw Error("Por favor Ingrese la razon de la Nota / Devolucion")
    }

    if (!invoiceData) {
      throw Error("La informacion de la factura no se ha cargado")
    }

    if (priceNote && !isValidAmount) {
      throw Error("El monto de la nota de credito no es correcta")
    }

    if (Number(documentSummaryState.descuento || 0) > 0) {
      creditnoteData.discountCode = invoiceData.discountCode
    }

    creditnoteData = {
      ...creditnoteData,
      description,
      exchange_rate: invoiceData.exchange_rate,
      activitycode: invoiceData.activitycode,
      selected_logo: invoiceData.selected_logo,
      clientId: invoiceData.clientId,
      currencyId: invoiceData.currencyId,
      subtotal: documentSummaryState.subtotal,
      descuento: documentSummaryState.descuento,
      impuestos: documentSummaryState.impuestos,
      total: documentSummaryState.total,
      created: getCurrentDateTime(),
      invoiceId: invoiceData.id
    }

    if (documentProducts) {
      const products = generateNewEDocumentProductsData(documentProducts, [...CreditNoteProductRelatedAttributes, "id"]) as CreditnoteProductDataType[]
      const createdCreditnote = await CreditnoteApi.createCreditnoteAndProducts(creditnoteData, products, !priceNote)

      if (easyposEInvoice && suscriptionData?.einvoice_status) {
        const tipoNotaDeCredito = priceNote ? "notaDeCredito" : "devolucion"
        const refsToSend: StoredReference[] = documentReferences.length > 0
          ? (documentReferences as StoredReference[])
          : [
              buildAutoReferenceFromInvoice(
                invoiceData,
                tipoNotaDeCredito,
                `${createdCreditnote.description || ""}`,
              ),
            ]
        if (createdCreditnote.id) {
          await CreditnoteApi.update(createdCreditnote.id, {
            info_references: serializeStoredReferences(refsToSend),
          })
        }
        await sendECreditnoteData(easyposEInvoice, `${createdCreditnote.id}`, toInfoRefElements(refsToSend))
      }

      clearForm()
      showAlert("success", "La Nota de credito ha sido creada existosamente")
      generateAndUploadPdf(`/user/creditnotes/print/${createdCreditnote.id}/0/1`, `${suscriptionData?.numero}/pdfs/Creditnote/${createdCreditnote.document_key}.pdf`)
      return createdCreditnote;
    }

    return {} as CreditnoteDataType;
  }

  const clearForm = async () => {
    dispatch(clearDocument())
    dispatch(clearDocumentReferences())
    loadDefaultData()
  }

  const getFormConfig = () => {
    const form = makeFormInputsUneditable(formConfig)

    const referenceInput = form.additionalSections.flatMap((s: any) => s.fields).flat().find((f: any) => f.name === "montoNota")
    if (referenceInput) {
      if (!priceNote) {
        delete referenceInput.validation
      } else {
        referenceInput.visibilityHiddenIf = () => false // make the field visible
      }
    }
    return form
  }

  const trigerMontoNotaChange = debounce((descuentoTotalNota: number) => {
    const documentProducts = eDocument.documentProducts || []
    const selectedLines = documentProducts.filter(p => p.selected == "true")
    const productTotal = getSelectProductTotal(selectedLines);

    if (productTotal.valueOf() < descuentoTotalNota) {
      showAlert("danger", "El monto total de las lineas seleccionadas es menor al valor de descuento, por favor revise la seleccion de lineas");
      setIsValidAmount(false)
      return false;
    }
    setIsValidAmount(true)
    dispatch(setDocumentNoteTotal(descuentoTotalNota))
  }, 500)

  const onInputUpdate = (name: keyof InvoiceFormData | "montoNota", value: any) => {
    if (name == "montoNota") {
      const descuentoTotalNota = Number(value || 0);
      trigerMontoNotaChange(descuentoTotalNota)
    }
  }

  const getCreateConfirmation = (): ConfirmationConfig | undefined => {
    const { ask_confirm = false } = suscriptionData || {}
    if (ask_confirm) {
      return {
        message: "Esta seguro que desea crear la Nota de Credito"
      }
    }
  }

  return (
    <>
      {formConfig?.sections && (<GenericForm<InvoiceFormData, InvoiceDataType>
        ref={formRef}
        formId={props.formId}
        confirmationConfig={getCreateConfirmation()}
        modelRelativeObjects={InvoiceRelatedAttributes}
        formConfig={getFormConfig()}
        getById={(...arg) => CreditnoteApi.get(...arg)}
        createFn={(...arg) => createCreditnote(...arg)}
        onInputChange={onInputUpdate}
      >
        <Row>
          <Col>
            <h3>
              Productos
            </h3>
          </Col>
        </Row>
        <EdocumentProductList
          documentNoteTable
          priceNote={priceNote} />
      </GenericForm >
      )}
      <LoadingScreen isLoading={isLoading} />
    </>

  )
}

export default CreditnoteForm;